// The signed-in person's own record — `/v1/me` (spec/08 §4.2, F-CMP-06, F-IAM-12).
//
//   PATCH  /v1/me                       marketing opt-in and opt-out, and starting a change of email
//                                       address or phone number (F-IAM-12)
//   POST   /v1/me/contact-verification  finishing it, with the code sent to the new address or number
//   GET    /v1/me/consents              what the player has agreed to: what is in force, and every row
//   POST   /v1/me/consents              accept a new version of the terms or the privacy policy
//   GET    /v1/me/sessions              the person's signed-in sessions, with device and IP (F-IAM-12)
//   DELETE /v1/me/sessions/{sessionId}  end one of them
//
// **Changing an email address or a phone number** (spec/07 §3.3) needs a fresh session — signed in
// within the last ten minutes (`freshAge`), or `403 reauthentication-required` — and the new value
// proved with a code sent to it; only then does it change, and a notice goes to the old one (N-14).
// Ending a session takes effect on that session's next request.
//
// A better-auth session is required — a cookie in a browser, `Authorization: Bearer` in the apps — and
// nothing else: these are the caller's own facts, in no organisation, so there is no organisation to
// name and no permission to hold. A player's own consent change is recorded by the consent rows
// themselves, not in the audit log, which is for staff and HQ actions (spec/07 §11, spec/20 §6).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { AuthSessionIdSchema, ConsentIdSchema, PlayerIdSchema } from "../ids/schema.js";
import { AcceptedDocumentSchema, ConsentKindSchema, ConsentSourceSchema, LegalDocumentVersionSchema, MarketingConsentSchema, } from "../primitives/consent.js";
import { OtpCodeSchema } from "../primitives/otp.js";
import { PhoneNumberSchema } from "../primitives/phone-number.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
const SIGNED_IN = 'Needs a signed-in user: a session cookie in a browser, `Authorization: Bearer` in the apps. It is ' +
    'about the caller only.';
/** The player's platform-level profile, as far as this contract has it so far (spec/07 §3.2). */
export const MyProfileSchema = z
    .object({
    id: PlayerIdSchema,
    username: z.string().nullable(),
    displayName: z.string().nullable(),
    /** Mirrors the marketing consent in force; the consent rows are the record of truth. */
    marketingOptIn: z.boolean(),
    /** The account's email address — never a synthetic `.invalid` one, which is `null` here. */
    email: z.string().nullable(),
    /** Whether that address has been proved with a code. */
    emailVerified: z.boolean(),
    /** E.164, when the account has a phone number. */
    phoneNumber: z.string().nullable(),
    /** Whether that number has been proved with a code. */
    phoneNumberVerified: z.boolean(),
    /**
     * `false` only for an account a Google, Discord or Steam round trip created, until its owner has chosen a gamer
     * tag and a name (`POST /v1/me/profile/complete`): a screen shows "Complete your profile" first (spec/07 §3.2).
     */
    profileComplete: z.boolean(),
})
    .openapi('MyProfile', { description: "The signed-in player's profile." });
/** An email address a person can receive mail at. A synthetic `.invalid` one is refused by the server. */
const NewEmailSchema = z.string().trim().min(3).max(254).regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'An email address');
export const UpdateMeSchema = z
    .object({
    /**
     * Switch marketing on or off. On writes a new consent row under the privacy policy version the
     * player last accepted; off stamps `revokedAt` on the grant in force, and takes effect at once.
     */
    marketingOptIn: z.boolean().optional(),
    /**
     * A new email address. Nothing changes yet: a code goes to the new address (`202`), and
     * `POST /v1/me/contact-verification` with it makes the change. Needs a fresh session.
     */
    email: NewEmailSchema.optional(),
    /** A new phone number, E.164. As with `email`: a code to the new number first, then the change. */
    phoneNumber: PhoneNumberSchema.optional(),
})
    .strict()
    .refine((body) => Object.keys(body).length > 0, { message: 'Send at least one field to change.' })
    .refine((body) => body.email === undefined || body.phoneNumber === undefined, {
    message: 'Change the email address or the phone number, one at a time.',
    path: ['phoneNumber'],
})
    .openapi('UpdateMe');
export const CONTACT_KINDS = ['email', 'phone'];
export const ContactVerificationStartedSchema = z
    .object({
    /** Which is changing. */
    contact: z.enum(CONTACT_KINDS),
    /** How the code travelled. */
    channel: z.enum(['email', 'sms', 'whatsapp']),
    /** The new address or number, masked — enough to recognise it, never the whole of it. */
    destination: z.string(),
    /** The code, and the change waiting on it, lapse then. */
    expiresAt: IsoDateTimeSchema,
    /** The profile as it stands: a marketing change sent in the same request is already made. */
    profile: MyProfileSchema,
})
    .openapi('ContactVerificationStarted', {
    description: 'A code is on its way to the new email address or phone number; nothing has changed yet.',
});
export const ConfirmContactChangeSchema = z
    .object({
    /** The code sent to the new address or number. Five wrong tries void it. */
    code: OtpCodeSchema,
})
    .strict()
    .openapi('ConfirmContactChange');
/**
 * One signed-in session: better-auth's `session` row, a sign-in on one device (spec/07 §3.3, P-20).
 * Never its token.
 */
export const SignedInSessionSchema = z
    .object({
    id: AuthSessionIdSchema,
    /** The session this request was made with. Ending it is signing out here. */
    current: z.boolean(),
    createdAt: IsoDateTimeSchema,
    /** When it was last refreshed — at most a day behind its last use (`updateAge`). */
    lastActiveAt: IsoDateTimeSchema,
    expiresAt: IsoDateTimeSchema,
    /** The address it signed in from, as the edge saw it (`cf-connecting-ip`). */
    ipAddress: z.string().nullable(),
    /** The client's own description of itself. */
    userAgent: z.string().nullable(),
    /** A short reading of it: "Chrome on Windows", "Clavian app on Android". */
    device: z.string().nullable(),
    /**
     * How it was signed in (`lastLoginMethod`): `phone-otp`, `email`, `username`, `email-otp`,
     * `password` (with a second factor), a social provider's id, `passkey`. `null` for a session made
     * before this was recorded.
     */
    loginMethod: z.string().nullable(),
    /** Whether it was established with a second factor (spec/07 §6.6). */
    secondFactor: z.boolean(),
})
    .openapi('SignedInSession', { description: 'One of your signed-in sessions: a sign-in on one device.' });
export const SignedInSessionListSchema = z
    .object({
    /** Newest first. */
    data: z.array(SignedInSessionSchema),
})
    .openapi('SignedInSessionList', { description: 'Your signed-in sessions that have not expired.' });
/** One consent row, exactly as recorded. Nothing in it is ever changed but `revokedAt`, once. */
export const ConsentRecordSchema = z
    .object({
    id: ConsentIdSchema,
    kind: ConsentKindSchema,
    version: LegalDocumentVersionSchema,
    grantedAt: IsoDateTimeSchema,
    /** When a marketing grant was withdrawn. Absent while it is in force. */
    revokedAt: IsoDateTimeSchema.optional(),
    source: ConsentSourceSchema,
})
    .openapi('ConsentRecord', { description: 'One consent row: what was agreed to, which version, when, and how.' });
export const MyConsentsSchema = z
    .object({
    /** The latest version of the terms the player accepted. Absent for an account that never did. */
    terms: AcceptedDocumentSchema.optional(),
    privacy: AcceptedDocumentSchema.optional(),
    marketing: MarketingConsentSchema,
    /** Every row ever written for this player, oldest first — the record of truth. */
    history: z.array(ConsentRecordSchema),
})
    .openapi('MyConsents', { description: 'What the signed-in player has agreed to, and the full record of it.' });
export const AcceptConsentsSchema = z
    .object({
    /** A version of the terms the player was shown and accepted. */
    terms: LegalDocumentVersionSchema.optional(),
    /** A version of the privacy policy the player was shown and accepted. */
    privacy: LegalDocumentVersionSchema.optional(),
})
    .strict()
    .refine((body) => body.terms !== undefined || body.privacy !== undefined, {
    message: 'Send the terms version, the privacy version, or both.',
})
    .openapi('AcceptConsents');
export const getMeRoute = createRoute({
    method: 'get',
    path: '/v1/me',
    operationId: 'getMe',
    tags: ['me'],
    summary: 'Your own profile',
    description: `${SIGNED_IN} The profile as it stands, with whether it still needs completing after a social sign-up.`,
    responses: {
        200: jsonResponse(MyProfileSchema, 'The profile.'),
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const CompleteMyProfileSchema = z
    .object({
    /**
     * The name venues and friends see, as the person wrote it (prefilled from the provider's). The gamer tag is chosen
     * first with `/v1/auth/update-user`, which checks it against every other one and offers alternatives.
     */
    displayName: z.string().trim().min(1).max(50),
    /**
     * The account's own name, when the person changes it here: what staff screens and the audit log show. The
     * organisation console sends it for a staff member (spec/13 §1); the player web leaves the provider's.
     */
    name: z.string().trim().min(1).max(100).optional(),
})
    .strict()
    .openapi('CompleteMyProfile');
export const completeMyProfileRoute = createRoute({
    method: 'post',
    path: '/v1/me/profile/complete',
    operationId: 'completeMyProfile',
    tags: ['me'],
    summary: 'Finish your profile after a social sign-up',
    description: `${SIGNED_IN} Saves the display name and marks the profile complete (spec/07 §3.2). Answers the same for a ` +
        'profile that is already complete, saving the name; the person’s own change writes no audit row (spec/07 §3.3).',
    request: { body: { content: { 'application/json': { schema: CompleteMyProfileSchema } }, required: true } },
    responses: {
        200: jsonResponse(MyProfileSchema, 'The profile, complete.'),
        400: problemResponse('The name is empty or too long, or a field is not accepted.', 'validation-failed'),
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateMeRoute = createRoute({
    method: 'patch',
    path: '/v1/me',
    operationId: 'updateMe',
    tags: ['me'],
    summary: 'Change your own profile: marketing, and your email address or phone number',
    description: `${SIGNED_IN} Marketing is off unless the player switches it on. Switching it off stamps \`revokedAt\` ` +
        'on the grant in force and stops marketing at once; switching it on again writes a new consent row. ' +
        'Transactional messages are not affected either way. A new `email` or `phoneNumber` changes nothing yet: ' +
        'it needs a session signed in within the last ten minutes, and a code goes to the new value (`202`), ' +
        'which `POST /v1/me/contact-verification` spends to make the change.',
    request: { body: { content: { 'application/json': { schema: UpdateMeSchema } }, required: true } },
    responses: {
        200: jsonResponse(MyProfileSchema, 'The profile as it now stands.'),
        202: jsonResponse(ContactVerificationStartedSchema, 'A code went to the new email address or phone number.'),
        400: problemResponse('The request could not be understood, the player has not accepted a privacy policy to opt in under, or ' +
            'the number cannot be used.', 'validation-failed', 'consent-required', 'phone-number-refused'),
        401: commonProblemResponses[401],
        403: problemResponse('Changing an email address or phone number needs a fresh sign-in. Or: a new phone number while phone sign-in is ' +
            'turned off on the Platform (`auth.phoneSignIn`) — no code goes to a phone, so nothing changed.', 'reauthentication-required', 'phone-sign-in-off'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('The code could not be sent; nothing changed.', 'otp-delivery-unavailable'),
    },
});
export const confirmContactChangeRoute = createRoute({
    method: 'post',
    path: '/v1/me/contact-verification',
    operationId: 'confirmContactChange',
    tags: ['me'],
    summary: 'Finish changing your email address or phone number, with the code sent to it',
    description: `${SIGNED_IN} From the session that asked for the change, within ten minutes. The new value replaces the ` +
        'old one, proved; a notice goes to the old one (N-14). Five wrong codes void it, and the change starts again.',
    request: { body: { content: { 'application/json': { schema: ConfirmContactChangeSchema } }, required: true } },
    responses: {
        200: jsonResponse(MyProfileSchema, 'The profile, with the new email address or phone number.'),
        400: problemResponse('The code is wrong, or there is no change waiting on one any more.', 'otp-invalid', 'otp-expired', 'validation-failed'),
        401: commonProblemResponses[401],
        403: problemResponse('A phone-number change while phone sign-in is turned off on the Platform (`auth.phoneSignIn`); nothing changed.', 'phone-sign-in-off'),
        409: problemResponse('The address or number, proved yours, is on another account.', 'contact-in-use'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listMySessionsRoute = createRoute({
    method: 'get',
    path: '/v1/me/sessions',
    operationId: 'listMySessions',
    tags: ['me'],
    summary: 'Where you are signed in',
    description: `${SIGNED_IN} Every session that has not expired, newest first, with the device, the address and how it ` +
        'was signed in. Read from the live session, never the cookie cache.',
    responses: {
        200: jsonResponse(SignedInSessionListSchema, 'Your sessions.'),
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const SessionParamsSchema = z.object({ sessionId: AuthSessionIdSchema }).openapi('AuthSessionParams');
export const revokeMySessionRoute = createRoute({
    method: 'delete',
    path: '/v1/me/sessions/{sessionId}',
    operationId: 'revokeMySession',
    tags: ['me'],
    summary: 'End one of your sessions',
    description: `${SIGNED_IN} The session stops working at once: its next request is \`401\`. Ending the one this request ` +
        'is made with signs you out here.',
    request: { params: SessionParamsSchema },
    responses: {
        204: { description: 'Ended.' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No such session of yours.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getMyConsentsRoute = createRoute({
    method: 'get',
    path: '/v1/me/consents',
    operationId: 'getMyConsents',
    tags: ['me'],
    summary: 'What you have agreed to',
    description: `${SIGNED_IN} The terms and privacy versions accepted, the marketing consent in force, and every ` +
        'consent row ever written for the player — the record a data export carries.',
    responses: {
        200: jsonResponse(MyConsentsSchema, "The player's consents."),
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const acceptConsentsRoute = createRoute({
    method: 'post',
    path: '/v1/me/consents',
    operationId: 'acceptConsents',
    tags: ['me'],
    summary: 'Accept a new version of the terms or the privacy policy',
    description: `${SIGNED_IN} Each version accepted is a new row; the row for the version accepted before is kept ` +
        'unchanged. Accepting a version already accepted changes nothing. Where the brand configuration names ' +
        'the documents in force, a version that is not the one in force is refused.',
    request: { body: { content: { 'application/json': { schema: AcceptConsentsSchema } }, required: true } },
    responses: {
        200: jsonResponse(MyConsentsSchema, "The player's consents, with the new acceptance."),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        409: problemResponse('The version sent is not the one in force; show the current document.', 'consent-version-outdated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=me.js.map